/**
 * productRouting.js
 *
 * Extracted from App.js — all host-based product routing in one place.
 * Previously embedded inside the main App function; now independently testable.
 *
 * To add a new product:
 *   1. Add its hostnames to STANDALONE_PRODUCT_HOSTS
 *   2. Add its URL to STANDALONE_PRODUCT_URLS
 *   3. Add its entry page to STANDALONE_PRODUCT_ENTRY_PAGES
 *   4. Add its page set to PRODUCT_PAGE_SETS
 */

export const STANDALONE_PRODUCT_HOSTS = {
  pagetone: new Set([
    "echo.rootsandbridge.com",
    "rnbstudio.rootsandbridge.com",
    "rootsandbridgestudio.rootsandbridge.com",
    "pagetone.rootsandbridge.com"
  ]),
  wonderworld: new Set(["wonderworld.rootsandbridge.com"]),
  livebridge: new Set(["livebridge.rootsandbridge.com"])
};

export const STANDALONE_PRODUCT_URLS = {
  pagetone: "https://pagetone.rootsandbridge.com",
  wonderworld: "https://wonderworld.rootsandbridge.com",
  livebridge: "https://livebridge.rootsandbridge.com"
};

export const STANDALONE_PRODUCT_ENTRY_PAGES = {
  pagetone: "readerLab",
  wonderworld: "wonderHome",
  livebridge: "liveBridge"
};

export const PRODUCT_PAGE_SETS = {
  wonderworld: new Set([
    "wonderHome",
    "about",
    "kid",
    "parent",
    "story",
    "question",
    "result",
    "summary",
    "finalEnding"
  ]),
  livebridge: new Set(["liveBridge", "liveBridgeInput", "liveBridgeViewer"]),
  pagetone: new Set(["readerLab"])
};

/**
 * Returns the product key for the given hostname, or null if it's the shared host.
 */
export function getStandaloneProductKey(hostname) {
  if (!hostname) {
    return null;
  }

  for (const [key, hosts] of Object.entries(STANDALONE_PRODUCT_HOSTS)) {
    if (hosts.has(hostname)) {
      return key;
    }
  }

  return null;
}

/**
 * Returns true if the given page belongs to the active standalone product.
 * When productKey is null (shared host) all pages are allowed.
 */
export function pageBelongsToStandaloneProduct(page, productKey) {
  if (!productKey) {
    return true;
  }

  return PRODUCT_PAGE_SETS[productKey]?.has(page) ?? false;
}

/**
 * Returns the current window hostname, lowercased.
 * Safe to call in SSR (returns "").
 */
export function getRuntimeHostname() {
  if (typeof window === "undefined") {
    return "";
  }

  return String(window.location.hostname || "").toLowerCase();
}

/**
 * Returns true if the runtime hostname is a rootsandbridge.com subdomain.
 * Used to decide whether standalone product deep-link URLs are safe to use.
 */
export function isRootsBridgeDomain(hostname) {
  return (
    hostname === "rootsandbridge.com" ||
    hostname === "www.rootsandbridge.com" ||
    hostname.endsWith(".rootsandbridge.com")
  );
}
